"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { marked } from "marked";
import "./ChatInterface.css";
import three60logo from "./three60logo.png";

const ChatInterface = ({ user, onLogout }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeChat, setActiveChat] = useState(0);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState("");
  const [theme, setTheme] = useState("dark");
  const [chatHistory, setChatHistory] = useState([]);
  const [currentModel, setCurrentModel] = useState("CV Expert");
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const modelDropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("chat-theme");
      if (savedTheme) {
        console.log("[v0] Loading saved theme:", savedTheme);
        setTheme(savedTheme);
        document.documentElement.setAttribute("data-theme", savedTheme);
      } else {
        const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
          .matches
          ? "dark"
          : "light";
        console.log("[v0] Using system theme:", systemTheme);
        setTheme(systemTheme);
        document.documentElement.setAttribute("data-theme", systemTheme);
        localStorage.setItem("chat-theme", systemTheme);
      }
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    console.log("[v0] Toggling theme from", theme, "to", newTheme);
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    localStorage.setItem("chat-theme", newTheme);
  };

  const handleAdminDashboard = () => {
    navigate("/dashboard");
  };

  // Available AI models
  const aiModels = [
    {
      id: "cv-expert",
      name: "CV Expert",
      description: "Specialized in resume and career advice",
      default: true,
    },
    {
      id: "tender-analyser",
      name: "Tender Analyser",
      description: "Focused on procurement and tender analysis",
    },
  ];

  const handleModelChange = (selectedModel) => {
    setCurrentModel(selectedModel.name);
    setIsModelDropdownOpen(false);
    const matchingChat = chatHistory.find(
      (chat) => chat.model === selectedModel.name
    );

    // If found, set active chat to that session, otherwise create new session (0)
    if (matchingChat) {
      setActiveChat(matchingChat.id);
    } else {
      setActiveChat(0); // This will trigger a new chat creation
    }
  };

  //Load Chat Sessions
  useEffect(() => {
    let isMounted = true;

    const fetchChatSessions = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/chats/sessions/user/${user.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!isMounted) return;
        console.log("Fetched chat sessions:", response.data);
        if (response.data.length > 0) {
          setChatHistory((prev) => [...prev, ...response.data]);
        }
      } catch (error) {
        console.error("Error fetching chat sessions:", error);
      }
    };

    fetchChatSessions();

    return () => {
      isMounted = false;
    };
  }, [user.id]);

  useEffect(() => {
    const currentChat = chatHistory.find((chat) => chat.id === activeChat);
    if (currentChat) {
      setMessages(currentChat.messages);
    } else {
      setMessages([]);
    }
  }, [activeChat]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const validateFiles = (files) => {
    const allowedTypes = [".pdf", ".docx", ".zip", ".pptx"];
    const maxFiles = 5;
    const validFiles = [];
    let errorMessage = "";

    if (files.length > maxFiles) {
      errorMessage = `Maximum ${maxFiles} files allowed`;
      return { validFiles: [], errorMessage };
    }

    for (const file of files) {
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      if (allowedTypes.includes(fileExtension)) {
        validFiles.push(file);
      } else {
        errorMessage = "Only PDF, DOCX, PPTX and ZIP files are supported";
        break;
      }
    }

    return { validFiles, errorMessage };
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const { validFiles, errorMessage } = validateFiles(files);

    if (errorMessage) {
      setFileError(errorMessage);
      setTimeout(() => setFileError(""), 3000);
      return;
    }

    const totalFiles = attachedFiles.length + validFiles.length;
    if (totalFiles > 5) {
      setFileError("Maximum 5 files allowed");
      setTimeout(() => setFileError(""), 3000);
      return;
    }

    setAttachedFiles((prev) => [...prev, ...validFiles]);
    setFileError("");
    console.log("Selected files:", attachedFiles);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    // Only hide overlay if leaving the main chat area completely
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    const { validFiles, errorMessage } = validateFiles(files);

    if (errorMessage) {
      setFileError(errorMessage);
      setTimeout(() => setFileError(""), 3000);
      return;
    }

    const totalFiles = attachedFiles.length + validFiles.length;
    if (totalFiles > 5) {
      setFileError("Maximum 5 files allowed");
      setTimeout(() => setFileError(""), 3000);
      return;
    }

    setAttachedFiles((prev) => [...prev, ...validFiles]);
    setFileError("");
  };

  const removeFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
    );
  };

  const getFileIcon = (filename) => {
    const extension = filename.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return "📄";
      case "docx":
        return "📝";
      case "zip":
        return "🗜️";
      default:
        return "📎";
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() && attachedFiles.length === 0) return;

    let currentActiveChat = activeChat;

    let title =
      inputValue.trim().substring(0, 30) +
      (inputValue.trim().length > 30 ? "..." : "");

    //console.log(title);

    // If no active chat, create a new one FIRST
    if (activeChat === 0) {
      try {
        currentActiveChat = await handleNewChat(title);
        console.log("New chat created with ID:", currentActiveChat);

        // Update activeChat state immediately
        setActiveChat(currentActiveChat);
      } catch (error) {
        console.error("Failed to create new chat:", error);
        return;
      }
    }

    // Wait a brief moment for state to update if needed
    await new Promise((resolve) => setTimeout(resolve, 0));

    const userMessage = {
      id: Date.now(),
      text: inputValue.trim(),
      sender: "user",
      timestamp: new Date(),
      files: attachedFiles.length > 0 ? [...attachedFiles] : undefined,
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);

    setChatHistory((prev) =>
      prev.map((chat) =>
        chat.id === currentActiveChat
          ? {
              ...chat,
              messages: newMessages,
              preview:
                chat.messages.length === 0 ||
                chat.preview === "New conversation"
                  ? inputValue.trim().substring(0, 30) +
                    (inputValue.trim().length > 30 ? "..." : "")
                  : chat.preview,
            }
          : chat
      )
    );

    const fileData =
      userMessage?.files?.length > 0
        ? userMessage.files.map((file) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            // If you upload files to server, add URL/path here later
          }))
        : [];

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/chats/message`,
        {
          session_id: currentActiveChat,
          message_text: userMessage.text,
          message_type: "user",
          files: fileData.length > 0 ? JSON.stringify(fileData) : null,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
    } catch (error) {
      console.error("Error sending message:", error);
    }

    setInputValue("");
    setAttachedFiles([]);
    setIsTyping(true);

    const apiURL =
      currentModel === "Tender Analyser"
        ? "https://n8n.three60energy.group/webhook/tender-analyser"
        : "https://n8n.three60energy.group/webhook/cv-expert";

    // API Call for AI response

    // Create FormData object
    const formData = new FormData();

    // Add text data
    formData.append("chatInput", userMessage.text);
    formData.append("sessionId", currentActiveChat);

    // Add files as binary data
    if (attachedFiles && attachedFiles.length > 0) {
      attachedFiles.forEach((file, index) => {
        let type = file.type;

        if (file.name.toLowerCase().endsWith(".zip")) {
          type = "application/zip";
        }

        const fixedFile = new File([file], file.name, { type });
        formData.append(`file${index}`, fixedFile);
      });
    }

    axios
      .post(apiURL, formData, {
        headers: {
          Authorization: `Bearer Secure`,
        },
      })
      .then(async (response) => {
        const aiReply = response.data.output;
        const aiMessage = {
          id: Date.now() + 1,
          text: marked(aiReply),
          sender: "ai",
          timestamp: new Date(),
        };
        const updatedMessages = [...newMessages, aiMessage];
        setMessages(updatedMessages);

        setChatHistory((prev) =>
          prev.map((chat) =>
            chat.id === activeChat
              ? { ...chat, messages: updatedMessages }
              : chat
          )
        );

        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/chats/message`,
            {
              session_id: currentActiveChat,
              message_text: marked(aiReply),
              message_type: "ai",
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        } catch (error) {
          console.error("Error sending message:", error);
        }

        setIsTyping(false);
      })
      .catch((error) => {
        console.error("Error fetching AI response:", error);
        const aiMessage = {
          id: Date.now() + 1,
          text: "Sorry, there was an error processing your request.",
          sender: "ai",
          timestamp: new Date(),
        };
        const updatedMessages = [...newMessages, aiMessage];
        setMessages(updatedMessages);
        setChatHistory((prev) =>
          prev.map((chat) =>
            chat.id === activeChat
              ? { ...chat, messages: updatedMessages }
              : chat
          )
        );
        setIsTyping(false);
      });
  };

  const handleNewChat = async (title) => {
    console.log("Creating new chat with title:", title);
    // Prevent issues if title is an event object
    if (title && typeof title === "object" && title.preventDefault) {
      title = "New conversation";
    }
    const newChatId = uuidv4();
    const newChat = {
      id: newChatId,
      preview: title,
      timestamp: new Date(),
      model: currentModel,
      messages: [],
    };
    setChatHistory((prev) => [newChat, ...prev]);
    setActiveChat(newChatId);
    setSidebarOpen(false);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/chats/sessions/create`,
        {
          user_id: user.id,
          session_id: newChatId,
          session_name: title,
          model: currentModel,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      console.log("Chat session created:", response.data);
      return newChatId; // Return the new chat ID
    } catch (error) {
      console.error("Error creating chat session:", error);
      throw error; // Re-throw to handle in calling function
    }
  };

  const handleChatSelect = (chatId) => {
    setActiveChat(chatId);
    setSidebarOpen(false);
  };

  const handleDeleteChat = (chatId, e) => {
    e.stopPropagation();
    if (chatHistory.length <= 1) return;

    const updatedChats = chatHistory.filter((chat) => chat.id !== chatId);

    setChatHistory(updatedChats);

    axios
      .delete(`${process.env.REACT_APP_API_URL}/chats/sessions/${chatId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((response) => {
        console.log("Chat session deleted:", response.data);
      })
      .catch((error) => {
        console.error("Error deleting chat session:", error);
        alert("Error deleting chat session. Please try again.");
      });

    if (activeChat === chatId) {
      const matchingChat = updatedChats.find(
        (chat) => chat.model === currentModel
      );

      // If found, set active chat to that session, otherwise create new session (0)
      if (matchingChat) {
        setActiveChat(matchingChat.id);
      } else {
        setActiveChat(0); // This will trigger a new chat creation
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  function formatSessionTime(timestamp) {
    // Convert to Date object if it's not already
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset time part for date comparison
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);

    if (compareDate.getTime() === today.getTime()) {
      return "Today";
    } else if (compareDate.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else {
      // Format as "Jan 15", "Feb 3", etc.
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  }

  return (
    <div className="chat-container" data-theme={theme}>
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`chat-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <h2>Chat History</h2>
          <button className="new-chat-button" onClick={handleNewChat}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Chat
          </button>
        </div>
        <div className="sidebar-content">
          {chatHistory.filter((chat) => chat.model === currentModel).length >
          0 ? (
            chatHistory
              .filter((chat) => chat.model === currentModel)
              .map((chat) => (
                <div
                  key={chat.id}
                  className={`chat-history-item ${
                    activeChat === chat.id ? "active" : ""
                  }`}
                  onClick={() => handleChatSelect(chat.id)}
                >
                  <div className="chat-preview">
                    <p>{chat.preview}</p>
                    <span className="chat-timestamp">
                      {formatSessionTime(new Date(chat.timestamp))}
                    </span>
                  </div>
                  <button
                    className="delete-chat-button"
                    onClick={(e) => handleDeleteChat(chat.id, e)}
                    title="Delete chat"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="3,6 5,6 21,6" />
                      <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
                    </svg>
                  </button>
                </div>
              ))
          ) : (
            <div className="no-chats-message">
              <div className="no-chats-icon">💬</div>
              <p>No chat history for {currentModel}</p>
            </div>
          )}
        </div>
      </aside>

      <div
        className={`chat-main-wrapper ${sidebarOpen ? "sidebar-open" : ""}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="main-chat-drag-overlay">
            <div className="main-drag-content">
              <div className="drag-icon">📎</div>
              <div className="drag-text">Drop files to attach</div>
              <div className="drag-subtext">(.pdf, .docx, .zip)</div>
            </div>
          </div>
        )}

        <header className="chat-header">
          <div className="header-left">
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title="Toggle sidebar"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div className="chat-title" style={{display: "flex", alignItems: "center", gap: "25px"}}>
              <img src={three60logo} alt="THREE60 Energy" width={200} height={51}/>
              <div>
                <h1>AI Assistant</h1>
                <span className="status-indicator">Online</span>
              </div>
            </div>
            <div className="model-selector-container" ref={modelDropdownRef}>
              <button
                className="model-selector-button"
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                title="Switch AI model"
              >
                <span className="current-model">
                  {currentModel}{" "}
                  <small>
                    {aiModels.find((model) => model.name === currentModel)
                      ?.default && "(Default)"}
                  </small>
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`dropdown-arrow ${
                    isModelDropdownOpen ? "open" : ""
                  }`}
                >
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </button>

              {isModelDropdownOpen && (
                <div className="model-dropdown">
                  {aiModels.map((model) => (
                    <button
                      key={model.id}
                      className={`model-option ${
                        currentModel === model.name ? "active" : ""
                      }`}
                      onClick={() => handleModelChange(model)}
                    >
                      <div className="model-info">
                        <span className="model-name">
                          {model.name} {model.default && "(Default)"}
                        </span>
                        <span className="model-description">
                          {model.description}
                        </span>
                      </div>
                      {currentModel === model.name && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="header-right">
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
            <button
              className="admin-button"
              onClick={handleAdminDashboard}
              title="Admin Dashboard"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            </button>
            <div className="user-info">
              <img
                src={
                  user.avatar ||
                  `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`
                }
                alt={user.name}
                className="user-avatar"
              />
              <span className="user-name">{user.name}</span>
            </div>
            <button className="logout-button" onClick={onLogout} title="Logout">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </header>

        <main className="chat-main">
          <div className="messages-container">
            {messages.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">💬</div>
                <h2>Start a conversation</h2>
                <p>
                  Ask me anything! I'm here to help with your questions and
                  provide assistance.
                </p>
                <div className="suggested-prompts">
                  <button
                    className="prompt-button"
                    onClick={() => setInputValue("What can you help me with?")}
                  >
                    What can you help me with?
                  </button>
                  <button
                    className="prompt-button"
                    onClick={() =>
                      setInputValue("Tell me about artificial intelligence")
                    }
                  >
                    Tell me about AI
                  </button>
                  <button
                    className="prompt-button"
                    onClick={() =>
                      setInputValue("How do I improve my productivity?")
                    }
                  >
                    Productivity tips
                  </button>
                </div>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <div key={message.id} className={`message ${message.sender}`}>
                    <div className="message-avatar">
                      {message.sender === "user" ? (
                        <img
                          src={
                            user.avatar ||
                            `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`
                          }
                          alt={user.name}
                        />
                      ) : (
                        <div className="ai-avatar">🤖</div>
                      )}
                    </div>
                    <div className="message-content">
                      <div className="message-bubble">
                        <p
                          dangerouslySetInnerHTML={{ __html: message.text }}
                        ></p>
                        {message.files && message.files.length > 0 && (
                          <div className="message-files">
                            {message.files.map((file, index) => (
                              <div key={index} className="message-file">
                                <span className="file-icon">
                                  {getFileIcon(file.name)}
                                </span>
                                <span className="file-name">{file.name}</span>
                                <span className="file-size">
                                  ({formatFileSize(file.size)})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="message-timestamp">
                        {formatTime(new Date(message.timestamp))}
                      </div>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="message ai">
                    <div className="message-avatar">
                      <div className="ai-avatar">🤖</div>
                    </div>
                    <div className="message-content">
                      <div className="message-bubble typing">
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </main>

        <footer className="chat-footer">
          <div className="input-container">
            {attachedFiles.length > 0 && (
              <div className="file-previews">
                {attachedFiles.map((file, index) => (
                  <div key={index} className="file-preview">
                    <span className="file-icon">{getFileIcon(file.name)}</span>
                    <div className="file-info">
                      <span className="file-name">
                        {file.name.length > 20
                          ? file.name.substring(0, 20) + "..."
                          : file.name}
                      </span>
                      <span className="file-size">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    <button
                      className="remove-file-button"
                      onClick={() => removeFile(index)}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {fileError && <div className="file-error">{fileError}</div>}

            <div className="input-wrapper">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="message-input"
                rows="1"
              />

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.zip,.pptx"
                multiple
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />

              <button
                className="attachment-button"
                onClick={() => fileInputRef.current?.click()}
                title="Attach files (.pdf, .docx, .zip)"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>

              <button
                className={`send-button ${
                  inputValue.trim() || attachedFiles.length > 0 ? "active" : ""
                }`}
                onClick={handleSendMessage}
                disabled={!inputValue.trim() && attachedFiles.length === 0}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22,2 15,22 11,13 2,9 22,2" />
                </svg>
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ChatInterface;
